import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CorporateIcon,
  FoldIcon,
  LocationChangeIcon,
  LogoutIcon,
  UnfoldIcon,
  UserEditIcon,
} from "src/assets/icons";
import { LocationSwitcherModal } from "src/components/LocationSwitcherModal";
import Monogram from "src/components/Monogram";
import { CircleSkeleton, Skeleton } from "src/components/Skeleton";
import { useAuth } from "src/context";
import { useDealers } from "src/context/DealersContext";
import { useClickOutsideModalListener } from "src/hooks/useClickOutsideModalListener";
import { useDealer } from "src/hooks/useDealer";
import { useDealerUserInfo } from "src/context/DealerUserInfoContext";

export default () => {
  const { dealer_id } = useDealer();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [hasMultipleLocations, setHasMultipleLocations] = useState(false);
  const ref = useRef(null);
  useClickOutsideModalListener(ref, () => setIsActive(false));
  const authObject = useAuth();
  const { dealerUserInfo, isLoading } = useDealerUserInfo();
  const { dealerOptions } = useDealers();

  useEffect(() => {
    if (dealerOptions.length > 1) {
      setHasMultipleLocations(true);
    } else {
      setHasMultipleLocations(false);
    }
  }, [dealerOptions]);

  const displayName = () => {
    const text =
      (dealerUserInfo.first_name ?? "") +
      " " +
      (dealerUserInfo.last_name ?? "");
    if (text.trim().length > 0) {
      return text;
    } else {
      return "[No Name Available]";
    }
  };
  const showDealerSettings = dealerUserInfo.role === "GENERAL_MANAGER";
  const showSwitchLocation = hasMultipleLocations;

  const getActiveHeight = () => {
    let height = 150;
    if (showSwitchLocation) height += 34;
    if (showDealerSettings) height += 34;
    return height;
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 fixed left-6 bottom-6 h-[40px] z-10">
        <CircleSkeleton width="40px" height="40px" />
        <div className="flex flex-col flex-1 gap-2">
          <Skeleton width="120px" height="16px" borderRadius="4px" />
          <Skeleton width="180px" height="12px" borderRadius="4px" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={ref}
        className={`
          flex flex-col p-2 fixed left-5 bottom-6 overflow-hidden z-10
          transition-all duration-[160ms] ease-in-out
          ${
            isActive
              ? `bg-white border border-[rgba(228,231,236,1)] rounded-[24px_12px_12px_12px] shadow-[0px_2px_8px_rgba(0,0,0,0.06),0px_16px_64px_-12px_rgba(0,0,0,0.1)] w-[300px]`
              : "bg-transparent border-transparent rounded-xl hover:bg-[rgba(228,231,236,1)] w-[232px] h-14"
          }
        `}
        style={isActive ? { height: `${getActiveHeight()}px` } : undefined}
      >
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className="flex flex-row justify-between items-center bg-transparent border-0 cursor-pointer"
        >
          {/* Monogrammed Detail Container */}
          <div className="flex overflow-hidden flex-row items-center">
            <Monogram size={40}>
              {dealerUserInfo.first_name?.charAt(0)}
            </Monogram>
            <div
              className={`flex flex-col gap-1 ml-2 text-[rgba(74,84,102,1)] items-start overflow-hidden ${
                isActive ? "w-[194px]" : "w-[143px]"
              }`}
            >
              <div className="overflow-hidden text-sm whitespace-nowrap text-ellipsis">
                {displayName()}
              </div>
              <div className="text-[11px] overflow-hidden whitespace-nowrap text-ellipsis leading-normal">
                {dealerUserInfo.email}
              </div>
            </div>
          </div>
          {isActive ? <UnfoldIcon /> : <FoldIcon />}
        </button>
        {isActive && (
          <>
            {/* Divider */}
            <div className="border-b border-[rgba(228,231,236,1)] mt-[15px] mb-2 mx-0" />

            {showSwitchLocation && (
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsLocationModalOpen(true);
                  setIsActive(false);
                }}
                className="flex flex-row items-center gap-4 p-2 no-underline rounded-md cursor-pointer hover:bg-[rgba(229,235,253,1)] active:bg-[rgba(221,227,245,1)]"
              >
                <div className="text-[rgba(74,84,102,1)] h-[18px] w-[18px]">
                  <LocationChangeIcon />
                </div>
                <div className="text-[rgba(17,24,39,1)] text-sm">
                  Switch Location
                </div>
              </Link>
            )}
            {dealerUserInfo.role === "GENERAL_MANAGER" && (
              <Link
                to={`/${dealer_id}/dealer-settings/preferences`}
                state={{
                  returnTo: `${location.pathname}${location.search}${location.hash}`,
                }}
                className="flex flex-row items-center gap-4 p-2 no-underline rounded-md cursor-pointer hover:bg-[rgba(229,235,253,1)] active:bg-[rgba(221,227,245,1)]"
              >
                <div className="text-[rgba(74,84,102,1)] h-[18px] w-[18px]">
                  <CorporateIcon />
                </div>
                <div className="text-[rgba(17,24,39,1)] text-sm">
                  Dealer Settings
                </div>
              </Link>
            )}
            <Link
              target="_blank"
              to="https://porchpass.atlassian.net/servicedesk/customer/portal/133/group/100/create/10266"
              className="flex flex-row items-center gap-4 p-2 no-underline rounded-md cursor-pointer hover:bg-[rgba(229,235,253,1)] active:bg-[rgba(221,227,245,1)]"
            >
              <div className="text-[rgba(74,84,102,1)] h-[18px] w-[18px]">
                <UserEditIcon />
              </div>
              <div className="text-[rgba(17,24,39,1)] text-sm">
                Request edit
              </div>
            </Link>
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                void authObject.logout();
              }}
              className="flex flex-row items-center gap-4 p-2 no-underline rounded-md cursor-pointer hover:bg-[rgba(229,235,253,1)] active:bg-[rgba(221,227,245,1)]"
            >
              <div className="text-[rgba(74,84,102,1)] h-[18px] w-[18px]">
                <LogoutIcon />
              </div>
              <div className="text-[rgba(17,24,39,1)] text-sm">Sign out</div>
            </Link>
          </>
        )}
      </div>
      <LocationSwitcherModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
      />
    </>
  );
};
